import { Box, Grid } from '@mui/material';
import { Field, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { isPromiseCancelError } from '../../../../core/utils/promise';
import { TargetType, TargetTypesEnum } from '../../models/target-type';
import FormApiError from '../../../../form/components/form-api-error/FormApiError';
import FormApiSuccess from '../../../../form/components/form-api-success/FormApiSuccess';
import HdFormikTextFieldValidator from '../../../../form/formik/components/text-field-with-async-validation/HdFormikTextFieldValidator';
import styles from '../../../../connector/components/reusable-styles/formik-wrapper-styles/FormikWrapperStyles.module.scss';
import HdButton from '../../../../button/components/button/HdButton';
import HdFormControl, {
  HdFormButtonWrapper
} from '../../../../form/components/form-control/HdFormControl';
import useQueue from '../../../../core/hooks/useQueue';
import ErrorFocus from '../../../../form/components/error-focus/ErrorFocus';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import ActivateTargetAPI from '../../ActivateTargetAPI';
import { ConfigByType } from './configByType';

const TARGET_NAME = 'TARGET_NAME';

interface ConfigTargetProps {
  formData: any;
  formMetaData: any;
  targetTypeIdentifier: TargetTypesEnum;
  targetTypeMetaData: TargetType;
  savedTargetName: string;
  formError: string;
  formSuccess: string;
  submitBtnText: string;
  testBtnText: string;
  submitting: boolean;
  connecting: boolean;
  canShowSubmissionOverlay: boolean;
  onFormChanges: Function;
  onContinueClick: Function;
  onContinueFormError: Function;
  onTestConnectionClick: Function;
  onTestConnection: Function;
  onTestConnectionFormError?: Function;
  onContinue: Function;
  markFormDirty: Function;
  isEditing: boolean;
  setFormSuccess: Function;
  formErrorCode: number;
  formErrorClick: Function;
}

export default function ConfigTarget({
  formData,
  formMetaData,
  targetTypeIdentifier,
  targetTypeMetaData,
  isEditing,
  savedTargetName,
  connecting,
  testBtnText,
  submitting,
  submitBtnText,
  onTestConnection,
  onContinue,
  onTestConnectionFormError,
  onContinueFormError,
  onTestConnectionClick,
  onContinueClick,
  formError,
  formErrorCode,
  formErrorClick,
  formSuccess,
  onFormChanges,
  markFormDirty,
  setFormSuccess,
  canShowSubmissionOverlay
}: ConfigTargetProps) {
  const formikRef = useRef<FormikProps<{}>>();
  // This ref is used to keep track of the callback < if any >  we want to invoke once the processQueue is empty
  const scheduleCallback = useRef<any>();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [_, addProcess, removeProcess, queueLength] = useQueue();
  const showOverLay = isConnecting || isSaving || canShowSubmissionOverlay;
  const targetConfigType = ConfigByType[targetTypeIdentifier];
  const FormComponent: any = targetConfigType.formComponent;

  useEffect(() => {
    if (queueLength === 0 && formikRef.current.isSubmitting) {
      formikRef.current.submitForm();
    }
  }, [queueLength]);

  const handleSubmit = (values, { setSubmitting }) => {
    if (queueLength) {
      return;
    }
    if (scheduleCallback.current) {
      scheduleCallback.current();
    } else {
      setSubmitting(false);
      onContinue();
      setIsSaving(false);
    }
  };

  const targetNameValidator = (inputValue, options) => {
    /**
     *  while editing target, we won't validate api call for the  current target's name.
     */
    if (isEditing && inputValue === savedTargetName) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return ActivateTargetAPI.validateTargetName(inputValue, options).catch(e => {
      if (isPromiseCancelError(e)) {
        return;
      }
      // eslint-disable-next-line no-throw-literal
      throw { ...e, customMessage: `A Target with name, <b>"${inputValue}"</b> already exists` };
    });
  };

  const handleTestConnection = () => {
    scheduleCallback.current = undefined;
    formikRef.current.setSubmitting(false);
    onTestConnection();
    setIsConnecting(false);
  };

  const onTestConnectionButtonClick = props => {
    onTestConnectionClick();
    props.validateForm().then(errorFields => {
      const keys = Object.keys(errorFields);
      if (keys.length) {
        onTestConnectionFormError(ConfigByType.getErroredFieldNames(targetTypeIdentifier, keys));
      } else {
        setIsConnecting(true);
      }
    });
    scheduleCallback.current = handleTestConnection;
    props.handleSubmit();
  };

  const onSaveButtonClick = props => {
    onContinueClick();
    props.validateForm().then(errorFields => {
      const keys = Object.keys(errorFields);
      if (keys.length) {
        onContinueFormError(ConfigByType.getErroredFieldNames(targetTypeIdentifier, keys));
      } else {
        setIsSaving(true);
      }
    });
  };

  const resetDefaults = () => {
    scheduleCallback.current = undefined;
    setIsConnecting(false);
    setIsSaving(false);
  };

  const initialValues = {
    ...targetConfigType.initialValues,
    ...formData,
    targetName: formData?.name || ''
  };

  const dataIdGenerator = getDataIdGenerator('targetConfig');

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ConfigByType.getValidationSchema(targetTypeIdentifier)}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {props => {
        const { targetName, ...values } = props.values;

        onFormChanges({
          ...values,
          name: targetName
        }); // informing state machine about the form changes

        if (props.dirty) {
          markFormDirty();
        }

        return (
          <Box
            component='form'
            noValidate
            onSubmit={props.handleSubmit}
            className={`${styles.formWrapper} ${
              (queueLength !== 0 && props.isSubmitting) || showOverLay ? styles.backdrop : ''
            }`}
          >
            <ErrorFocus formik={props} onFormError={resetDefaults} />

            <Box className={styles.formCard}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <HdFormControl>
                    <Field
                      name='targetName'
                      label='Target Name'
                      component={HdFormikTextFieldValidator}
                      asyncValidator={targetNameValidator}
                      helperText='A unique name for your Target'
                      autoFocus
                      required
                      maxLength={255}
                      asyncMonitor={(isProcessGoingOn: boolean) => {
                        if (isProcessGoingOn) {
                          addProcess(TARGET_NAME);
                        } else {
                          removeProcess(TARGET_NAME);
                        }
                      }}
                    />
                  </HdFormControl>
                </Grid>
              </Grid>

              <FormComponent
                isEditing={isEditing}
                targetTypeMetaData={targetTypeMetaData}
                formData={formData}
                formMetaData={formMetaData}
              />

              {formError ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} className={styles.infoWrapper}>
                    <FormApiError
                      message={formError}
                      errorCode={formErrorCode}
                      nodeType={targetTypeIdentifier}
                      nodeCategory='TARGET'
                      docLink={targetTypeMetaData.docLink}
                      onClick={formErrorClick}
                      dataId={dataIdGenerator('')}
                    />
                  </Grid>
                </Grid>
              ) : null}

              {formSuccess ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} className={styles.infoWrapper}>
                    <FormApiSuccess
                      message={formSuccess}
                      onClose={() => {
                        setFormSuccess(undefined);
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container direction='row' justifyContent='center' alignItems='center'>
                <HdFormButtonWrapper>
                  <HdButton
                    variation='outline'
                    palette='secondary'
                    showProgress={isConnecting || connecting}
                    onClick={() => onTestConnectionButtonClick(props)}
                    dataId={dataIdGenerator('test')}
                  >
                    {isConnecting ? 'Connecting' : testBtnText}
                  </HdButton>
                </HdFormButtonWrapper>

                <HdFormButtonWrapper>
                  <HdButton
                    type='submit'
                    showProgress={isSaving || submitting}
                    onClick={() => onSaveButtonClick(props)}
                    dataId={dataIdGenerator('save')}
                  >
                    {isSaving ? 'Saving' : submitBtnText}
                  </HdButton>
                </HdFormButtonWrapper>
              </Grid>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
}
